import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Schedule,
  Warning,
  MonetizationOn,
  Info,
  CheckCircle,
  Delete,
  Refresh,
  FilterList,
  Clear
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth } from '../../contexts/AuthContext';
import { ExpensesService } from '../../utils/expenses-service';
import {
  ExpenseNotification,
  ExpenseNotificationsResponse
} from '../../types';

interface ExpenseNotificationHistoryProps {
  className?: string;
}

const ExpenseNotificationHistory: React.FC<ExpenseNotificationHistoryProps> = ({
  className = ''
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notifications, setNotifications] = useState<ExpenseNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    priority: '',
    startDate: null as Date | null,
    endDate: null as Date | null
  });

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<string | null>(null);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);



      // Build options object for API call
      const options: any = {
        page,
        limit: 20
      };

      // Tab-based filtering
      switch (currentTab) {
        case 0: // All
          break;
        case 1: // Unread
          options.status = 'pending,sent';
          break;
        case 2: // Read
          options.status = 'read';
          break;
        case 3: // Dismissed
          options.status = 'dismissed';
          break;
      }

      // Apply additional filters
      if (filters.type) options.type = filters.type;
      if (filters.status && currentTab === 0) options.status = filters.status;
      if (filters.priority) options.priority = filters.priority;
      if (filters.startDate) options.startDate = filters.startDate.toISOString();
      if (filters.endDate) options.endDate = filters.endDate.toISOString();

      const response = await ExpensesService.getExpenseNotifications(options);

      setNotifications(response.notifications);
      setTotalPages(Math.ceil(response.totalCount / 20));
    } catch (err) {
      console.error('Error loading notification history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [currentTab, page, filters]);

  useEffect(() => {
    if ((user as any)?.userGroup === 'hosts') {
      loadNotifications();
    }
  }, [user, loadNotifications]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      await ExpensesService.dismissExpenseNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.notificationId !== notificationId));
      setDeleteConfirmOpen(false);
      setNotificationToDelete(null);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      status: '',
      priority: '',
      startDate: null,
      endDate: null
    });
    setPage(1);
  };

  const getNotificationIcon = (type: ExpenseNotification['type']) => {
    switch (type) {
      case 'expense_reminder':
        return <Schedule color="primary" />;
      case 'expense_overdue':
        return <Warning color="error" />;
      case 'recurring_expense_created':
        return <MonetizationOn color="success" />;
      case 'expense_report_ready':
        return <Info color="info" />;
      default:
        return <Info />;
    }
  };

  const getStatusIcon = (status: ExpenseNotification['status']) => {
    switch (status) {
      case 'read':
        return <CheckCircle color="success" fontSize="small" />;
      case 'dismissed':
        return <Clear color="disabled" fontSize="small" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority: ExpenseNotification['priority']) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTypeLabel = (type: ExpenseNotification['type']) => {
    switch (type) {
      case 'expense_reminder':
        return 'Expense Reminder';
      case 'expense_overdue':
        return 'Overdue Expense';
      case 'recurring_expense_created':
        return 'Recurring Expense Created';
      case 'expense_report_ready':
        return 'Report Ready';
      default:
        return 'Notification';
    }
  };

  if ((user as any)?.userGroup !== 'hosts') {
    return null;
  }

  return (
    <Paper className={className} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">
          Expense Notification History
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<FilterList />}
            onClick={() => setFilterOpen(true)}
            variant="outlined"
            size="small"
          >
            Filters
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={loadNotifications}
            variant="outlined"
            size="small"
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="All" />
        <Tab label="Unread" />
        <Tab label="Read" />
        <Tab label="Dismissed" />
      </Tabs>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Loading notifications...
          </Typography>
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No notifications found
          </Typography>
        </Box>
      ) : (
        <>
          <List>
            {notifications.map((notification) => (
              <ListItem
                key={notification.notificationId}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: notification.status === 'read' ? 'transparent' : 'action.hover'
                }}
              >
                <ListItemIcon>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" component="span">
                        {notification.title}
                      </Typography>
                      <Chip
                        label={getTypeLabel(notification.type)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={notification.priority}
                        size="small"
                        color={getPriorityColor(notification.priority)}
                        variant="outlined"
                      />
                      {getStatusIcon(notification.status)}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {notification.message}
                      </Typography>

                      {notification.expenseData && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip
                            label={notification.expenseData.categoryName}
                            size="small"
                            variant="outlined"
                          />
                          {notification.expenseData.listingTitle && (
                            <Chip
                              label={notification.expenseData.listingTitle}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        Created: {formatDateTime(notification.createdAt)}
                        {notification.readAt && (
                          <> • Read: {formatDateTime(notification.readAt)}</>
                        )}
                        {notification.dismissedAt && (
                          <> • Dismissed: {formatDateTime(notification.dismissedAt)}</>
                        )}
                      </Typography>
                    </Box>
                  }
                />

                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setNotificationToDelete(notification.notificationId);
                      setDeleteConfirmOpen(true);
                    }}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Filter Dialog */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Filter Notifications</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="expense_reminder">Expense Reminder</MenuItem>
                <MenuItem value="expense_overdue">Overdue Expense</MenuItem>
                <MenuItem value="recurring_expense_created">Recurring Expense Created</MenuItem>
                <MenuItem value="expense_report_ready">Report Ready</MenuItem>
              </Select>
            </FormControl>

            {currentTab === 0 && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="dismissed">Dismissed</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                label="Priority"
              >
                <MenuItem value="">All Priorities</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <DatePicker
              label="Start Date"
              value={filters.startDate}
              onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
              slotProps={{ textField: { fullWidth: true } }}
            />

            <DatePicker
              label="End Date"
              value={filters.endDate}
              onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear Filters</Button>
          <Button onClick={() => setFilterOpen(false)}>Cancel</Button>
          <Button onClick={() => setFilterOpen(false)} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this notification? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => notificationToDelete && handleDeleteNotification(notificationToDelete)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExpenseNotificationHistory;